"use client";

import React, { useEffect, useId, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { AgentThinkingTraceStep } from "@/types/createAgent";
import { presentThinkingStep } from "@/lib/agent/presentThinkingTrace";
import "./agent-activity.css";

interface ThinkingTraceProps {
  steps: AgentThinkingTraceStep[];
  /** Turn still in flight; completed traces remain above their reply. */
  isActive: boolean;
}

function ElapsedTime({ startedAt }: { startedAt: number }) {
  const getSeconds = () =>
    Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  const [seconds, setSeconds] = useState(getSeconds);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [startedAt]);

  return (
    <span>
      {seconds < 60
        ? `${seconds}s`
        : `${Math.floor(seconds / 60)}m ${seconds % 60}s`}{" "}
      elapsed
    </span>
  );
}

function ThinkingTraceDisclosure({ steps, isActive }: ThinkingTraceProps) {
  const [expanded, setExpanded] = useState(false);
  const activityId = useId();
  const currentStep = steps.reduce(
    (latest, step) => (step.state === "running" ? step : latest),
    steps[steps.length - 1]
  );
  const currentLabel = isActive
    ? presentThinkingStep(currentStep).summary
    : "Designer activity";

  return (
    <div
      data-agent-thinking-trace={isActive ? "active" : "settled"}
      className="agent-activity"
    >
      <button
        type="button"
        className="agent-activity-toggle"
        aria-expanded={expanded}
        aria-controls={activityId}
        onClick={() => setExpanded(value => !value)}
      >
        <span
          role={isActive ? "status" : undefined}
          aria-live={isActive ? "polite" : "off"}
          aria-atomic="true"
          className="agent-activity-status"
        >
          {currentLabel}
        </span>
        <ChevronRight className="agent-activity-chevron" aria-hidden="true" />
      </button>
      <div
        id={activityId}
        hidden={!expanded}
        className="agent-activity-details"
      >
        <div className="agent-activity-meta">
          <span>
            {steps.length} {steps.length === 1 ? "step" : "steps"}
          </span>
          {expanded && isActive && (
            <ElapsedTime startedAt={steps[0].timestamp} />
          )}
        </div>
        <ol className="agent-activity-steps" aria-label="Designer activity">
          {steps.map(step => {
            const copy = presentThinkingStep(step);
            return (
              <li key={step.id} data-agent-loop-message>
                <div className="agent-activity-step-heading">
                  <span className="agent-activity-step-title">
                    {copy.title}
                  </span>
                  <span className="agent-activity-step-state">
                    {isActive && step.state === "running"
                      ? "In progress"
                      : step.source === "tool" && step.outcome === "ok"
                        ? "Done"
                        : "Considered"}
                  </span>
                </div>
                <p>{copy.summary}</p>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

/**
 * Keep the current activity in one quiet line and let customers reopen the
 * same details after the reply arrives.
 */
export default function ThinkingTrace({ steps, isActive }: ThinkingTraceProps) {
  if (steps.length === 0) {
    return null;
  }

  return (
    <ThinkingTraceDisclosure
      key={steps[0].id}
      steps={steps}
      isActive={isActive}
    />
  );
}
